import React, { FC, useEffect } from "react";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react";
import {  ContactService } from "../../../shared/services";
import {
  AuthService,
  ITableHeaders,
  TableHeader,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  ContactStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { ContactGridModel } from "../../../shared/models";
import {
  DashboardTopBar,
  DashboardTopRightSide,
  ItemContentArea,
  RenderPaymentMethod,
  TableGrid,
} from "../../../shared/components";
import { GridNames, ItemDropDownOptions } from "../../../shared/enums";
import { AccountSettingsStore } from "../../../shared/store";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";
import { debounceTime, Subscription } from "rxjs";

type IProps = {
  contactStore: ContactStore;
  navigate: NavigateFunction;
  itemStore: ItemsStore;
  merchantAccountStore: MerchantAccountStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
};

const ContactGrid: FC<IProps> = ({
  contactStore,
  navigate,
  itemStore,
  merchantAccountStore,
  rolesAndPermissionStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);
  const [firstTimeLoad, setFirstTimeLoad] = React.useState(true);

  useEffect(() => {
    itemStore.updateSearchValue("", ItemDropDownOptions.Contacts);
    loadData(
      contactStore.pagination.pageNumber,
      contactStore.pagination.pageSize,
      contactStore.searchValue,
      true
    );

    const subscription: Subscription = contactStore.searchSubject
      .pipe(debounceTime(500))
      .subscribe(() => {
        loadData(
          contactStore.pagination.pageNumber,
          contactStore.pagination.pageSize,
          contactStore.searchValue
        );
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  function loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    setFirstTimeLoad(firstTimeLoad);
    setShowLoader(true);
    ContactService.getContacts(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (response) => {
        setShowLoader(false);
        contactStore.deserializeGridData(response.result || [], response.meta);
      },
      error: (error) => {
        setShowLoader(false);
        console.log("error", error);
      },
    });
  }

  function onPaginationChange(pageNumber: number, pageSize: number) {
    loadData(pageNumber, pageSize, contactStore.searchValue);
  }

  function goToDetailPage(id: string) {
    navigate(`/app/contacts/detail/${id}`);
  }

  function columns() {
    const columns: ITableHeaders<ContactGridModel>[] = [
      {
        dataKey: "firstName",
        title: "Name",
        onCellRender: (data: ContactGridModel) => {
          return data.firstName + " " + data.lastName;
          // return <CustomButton>{data.firstName}</CustomButton>;
        },
        cellClassName: "table-col-20",
        className: "table-col-20",
      },
      {
        dataKey: "paymentMethod",
        title: "Payment Method",
        cellClassName: "clickable",
        onCellRender: (data: ContactGridModel) => {
          return (
            data.paymentMethod && (
              <RenderPaymentMethod paymentMethod={data.paymentMethod} />
            )
          );
        },
      },
      {
        dataKey: "totalSpent",
        title: "Total Spend",
        cellClassName: "table-col-right",
        className: "table-col-right",
        onCellRender: (data: ContactGridModel) => {
          return "$" + AccountSettingsStore.formatAmount(data.totalSpent);
        },
      },
      {
        dataKey: "createdDate",
        title: "Created Date",
        onCellRender: (data: ContactGridModel) => {
          return UtilityMethods.formatDateInString(data.createdDate);
        },
      },
      {
        dataKey: "homeEmail",
        title: "Email",
      },
      {
        dataKey: "homePhone",
        title: "Phone Number",
        onCellRender: (data: ContactGridModel) => {
          if (data.mobilePhone) {
            return (
              "+" +
              data.mobilePhone?.countryCode +
              " " +
              data.mobilePhone?.phoneNumber
            );
          }
          if (data.homePhone) {
            return (
              "+" +
              data.homePhone?.countryCode +
              " " +
              data.homePhone?.phoneNumber
            );
          }
          if (data.workPhone) {
            return (
              "+" +
              data.workPhone?.countryCode +
              " " +
              data.workPhone?.phoneNumber
            );
          }
          if (data.otherPhone) {
            return (
              "+" +
              data.otherPhone?.countryCode +
              " " +
              data.otherPhone?.phoneNumber
            );
          }
          return <></>;
        },
      },
    ];
    return columns;
  }

  const loadContactData = () => {
    loadData(
      contactStore.pagination.pageNumber,
      contactStore.pagination.pageSize,
      contactStore.searchValue
    );
  };

  const isNoData =
    firstTimeLoad &&
    contactStore.pagination.totalItemCount === 0 &&
    !showLoader &&
    contactStore.searchValue === "";

  function renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide
        itemStore={itemStore}
        showSearchInput={!isNoData}
        showButton={!isNoData}
        onRefresh={() => loadContactData()}
      />
    ) : (
      <></>
    );
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<>{showLoader && firstTimeLoad ? null : content}</>}
          merchantAccountStore={merchantAccountStore}
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          showLoader={showLoader}
          // className={isNoData ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  return renderDetails(
    <TableGrid
      height={
        contactStore.showFilter
          ? "calc(100vh - 499px)"
          : merchantAccountStore.hasNotificationBar()
            ? "calc(100vh - 350px)"
            : ""
      }
      tableHeader={
        <TableHeader
          showFilter={contactStore.showFilter}
          onToggleFilter={() => contactStore.toggleFilter()}
          total={contactStore.pagination.totalItemCount}
        />
      }
      totalItemCount={contactStore.pagination.totalItemCount}
      onRowClick={(data: ContactGridModel) => goToDetailPage(data.id)}
      cellSpacing={20}
      onPaginationChange={(pageNumber: number, pageSize: number) =>
        onPaginationChange(pageNumber, pageSize)
      }
      pagination={contactStore.pagination}
      data={contactStore.gridData}
      headers={columns()}
      name={GridNames.Contact}
      hideTable={isNoData}
      onRefresh={() => loadContactData()}
      rolesAndPermissionStore={rolesAndPermissionStore}
      countriesWithState={itemStore.countryStore.countriesWithState}
      // height="calc(100vh - 508px)"
    />
  );
};

export default observer(ContactGrid);
